import { ButtonHTMLAttributes, forwardRef, ReactNode } from "react";

import { cn } from "@/utils";
import { Loader } from "@/components";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: "s" | "m" | "l";
  loading?: boolean;
  icon?: ReactNode;
  iconPosition?: "left" | "right";
  variant?:
    | "primary"
    | "secondary"
    | "secondary_focussed"
    | "tertiary"
    | "tertiaryHighlight"
    | "tertiaryDanger"
    | "edit"
    | "link"
    | "danger";
}

const VARIANTS: Record<string, string> = {
  primary:
    "bg-highlightPrimary text-white hover:bg-highlightSecondary active:bg-highlightTertiary",
  secondary:
    "border border-border text-item-primary bg-none hover:border-border-hover hover:bg-surface-secondary active:border-item-primary",
  secondary_focussed:
    "border bg-highlightBg border-highlightPrimary text-highlightPrimary hover:text-highlightSecondary hover:border-highlightSecondary hover:bg-highlightBgSecondary",
  tertiary:
    "text-item-primary hover:bg-surface-secondary active:bg-surface-tertiary",
  tertiaryHighlight:
    "text-highlightPrimary hover:text-highlightSecondary hover:bg-highlightBg active:text-highlightTertiary active:bg-highlightBgSecondary",
  tertiaryDanger: "text-error-item hover:bg-error-surface",
  edit: "active:text-highlightTertiary active:bg-highlightBgTertiary rounded-2xl bg-highlightBg text-highlightPrimary hover:bg-highlightBgSecondary hover:text-highlightSecondary",
  link: "text-item-primary underline-offset-4 hover:underline",
  danger: "bg-error-item text-white hover",
};
const DISABLED_VARIANTS: Record<string, string> = {
  primary: "bg-surface-disabled text-item-invert-primary",
  secondary: "border-border text-item-tertiary bg-none",
  secondary_focussed: "bg-surface-tertiary border-border text-item-secondary",
  tertiary: "text-item-tertiary bg-none",
  tertiaryHighlight: "text-item-tertiary bg-none",
  tertiaryDanger: "text-item-tertiary bg-none",
};
const SIZES: Record<string, string> = {
  s: "h-8 rounded-[10px] px-[10px] caption",
  m: "h-[38px] rounded-[10px] px-3 p2-semibold",
  l: "h-11 rounded-[10px] px-[14px] p1-semibold",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className = "",
      variant,
      size,
      children,
      loading,
      icon,
      iconPosition,
      ...props
    },
    ref,
  ) => {
    const iconElement = (
      <>
        {loading ? (
          <Loader className={iconPosition === "left" ? "mr-1" : "ml-1"} />
        ) : icon ? (
          <>{!children ? icon : <div className="mr-1">{icon}</div>}</>
        ) : null}
      </>
    );

    return (
      <button
        ref={ref}
        className={cn([
          "inline-flex items-center justify-center whitespace-nowrap rounded-md " +
            "ring-offset-grey50 transition-colors " +
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-item-secondary " +
            "focus-visible:ring-offset-2 " +
            "disabled:pointer-events-none",
          SIZES[size ?? "m"],
          VARIANTS[variant ?? "primary"],
          props.disabled && DISABLED_VARIANTS[variant ?? "primary"],
          className,
        ])}
        type="button"
        {...props}
      >
        {iconPosition === "left" && <span>{iconElement}</span>}
        {children}
        {iconPosition !== "left" && <span>{iconElement}</span>}
      </button>
    );
  },
);
Button.displayName = "Button";
